import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { groupBy, uniq } from 'lodash'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { useRealms } from '../config/queries'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'
import Select from './Select'

interface Props {
  setRealm: (value: wowaudit.Realm | undefined) => void
  realm?: wowaudit.Realm
  inModal?: boolean
  isClearable?: boolean
  className?: string
  disabled?: boolean
  // Renders as segments of a surrounding bordered control instead of separate boxes.
  joined?: boolean
}

const RulesetSelect = ({
  setRealm,
  realm,
  className,
  isClearable,
  inModal = false,
  disabled = false,
  joined = false,
}: Props) => {
  const { t } = useTranslation()
  const { data: realms } = useRealms()
  const [region, setRegion] = useState<string>(realm?.region ?? 'EU')

  useEffect(() => {
    if (realm) setRegion(realm.region)
  }, [realm])

  const regions = useMemo(
    () => uniq((realms || []).map((realmOption) => realmOption.region)),
    [realms],
  )
  const rulesets = (realms || []).filter(
    (realmOption) => realmOption.region === region,
  )

  return (
    <div className={cn('flex', !joined && 'gap-2', className)}>
      <Select
        value={region}
        setValue={(value) => {
          if (!value) return
          setRegion(value)
          if (realm && value !== realm.region) {
            setRealm(
              realms?.find(
                (realmOption) =>
                  realmOption.region === value &&
                  realmOption.slug === realm.slug,
              ),
            )
          }
        }}
        options={regions.map((value) => ({ value, label: value }))}
        disabled={disabled}
        inModal={inModal}
        className={cn('w-24 shrink-0', joined && 'border-0 rounded-none')}
      />
      <Select
        value={realm?.id.toString()}
        setValue={(value) =>
          setRealm(rulesets.find((option) => option.id.toString() === value))
        }
        options={rulesets.map((option) => ({
          value: option.id.toString(),
          label: option.name,
        }))}
        placeholder={
          isClearable
            ? t('realm-select.any-ruleset')
            : t('realm-select.ruleset')
        }
        disabled={disabled}
        isClearable={isClearable}
        inModal={inModal}
        className={cn(
          'w-32 grow min-w-max',
          joined && 'border-0 border-l-2 rounded-l-none',
        )}
      />
    </div>
  )
}

const RealmSearchSelect = ({
  setRealm,
  realm,
  className,
  isClearable,
  inModal = false,
  disabled = false,
}: Props) => {
  const { t } = useTranslation()
  const gameVersion = useGameVersion()
  const { data: realms } = useRealms()
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const minimumSearchLength = gameVersion.config.realm_search_min_length

  const realmsByRegion = useMemo(() => {
    if (realms) {
      return Object.entries(groupBy(realms, (realm) => realm.region))
    } else {
      return []
    }
  }, [realms])

  return (
    <Popover open={open} onOpenChange={setOpen} modal={inModal}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          disabled={disabled}
          aria-expanded={open}
          className={`w-45 justify-between ${className}`}
        >
          <span
            className={`${realm ? '' : 'text-muted-foreground'} font-normal`}
          >
            {realm ? (
              <span className='max-w-[8.3rem] truncate block'>
                <span className='text-muted-foreground mr-1 text-xs'>
                  {realm.region}
                </span>{' '}
                {realm.name}
              </span>
            ) : isClearable ? (
              'Any realm'
            ) : (
              'Select realm'
            )}
          </span>
          {realm && isClearable ? (
            <X
              className='ml-2 mr-[-0.3rem] opacity-50 hover:opacity-100 transition-opacity p-1'
              onClick={(e) => {
                e.preventDefault()
                setRealm(undefined)
              }}
            />
          ) : (
            <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-48 p-0' style={{ pointerEvents: 'auto' }}>
        <Command
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1
            return 0
          }}
        >
          <CommandInput
            placeholder={t('realm-select.search-placeholder')}
            value={term}
            onValueChange={setTerm}
          />
          <CommandList>
            {term.length >= minimumSearchLength && (
              <CommandEmpty>No realm found.</CommandEmpty>
            )}
            {(term.length >= minimumSearchLength
              ? realmsByRegion
              : realm
              ? ([[realm.region, [realm]]] as [string, wowaudit.Realm[]][])
              : []
            ).map(([region, realms]) => (
              <CommandGroup key={region} heading={region}>
                {realms.map((realmOption) => (
                  <CommandItem
                    key={realmOption.id}
                    value={realmOption.nameWithRegion}
                    onSelect={() => {
                      setRealm(realm === realmOption ? undefined : realmOption)
                      setOpen(false)
                    }}
                  >
                    <div className='flex items-center'>
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          realmOption === realm ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {realmOption.name}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const RealmSelect = (props: Props) => {
  const gameVersion = useGameVersion()

  return gameVersion.has('realm_rulesets') ? (
    <RulesetSelect {...props} />
  ) : (
    <RealmSearchSelect {...props} />
  )
}

export default RealmSelect
